import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { toast } from "react-toastify";
import {
  getDashboardErrorMessage,
  unwrapDashboardResponse,
} from "../../api/dashboardResponse";
import { addAsyncCases } from "./asyncReducers";
import {
  paginationInitialState,
  paginationReducers,
} from "./paginationReducers";

export const createEntityListInitialState = (extraState = {}) => ({
  data: null,
  ui: {
    loading: false,
    msg: "",
  },
  pagination: {
    ...paginationInitialState,
  },
  status: "",
  error: null,
  ...extraState,
});

export const rejectDashboardRequest = (
  error,
  rejectWithValue,
  fallbackMessage
) => rejectWithValue(getDashboardErrorMessage(error, fallbackMessage));

export const createDashboardQueryThunk = (
  typePrefix,
  apiRequest,
  fallbackMessage
) =>
  createAsyncThunk(typePrefix, async (params, { rejectWithValue }) => {
    try {
      const response = await apiRequest(params);
      return unwrapDashboardResponse(response, fallbackMessage);
    } catch (error) {
      return rejectDashboardRequest(error, rejectWithValue, fallbackMessage);
    }
  });

const resolveSuccessMessage = (getSuccessMessage, params, payload) => {
  if (typeof getSuccessMessage === "function") {
    return getSuccessMessage(params, payload);
  }

  return getSuccessMessage || null;
};

export const createDashboardMutationThunk = (
  typePrefix,
  apiRequest,
  { fallbackMessage, getSuccessMessage } = {}
) =>
  createAsyncThunk(typePrefix, async (params, { rejectWithValue }) => {
    try {
      const response = await apiRequest(params);
      const payload = unwrapDashboardResponse(response, fallbackMessage);
      const successMessage = resolveSuccessMessage(
        getSuccessMessage,
        params,
        payload
      );

      if (successMessage) {
        toast.success(successMessage);
      }

      return payload || null;
    } catch (error) {
      const message = getDashboardErrorMessage(error, fallbackMessage);
      toast.error(message);
      return rejectWithValue(message);
    }
  });

export const updateEntityListState = (state, action, listKey) => {
  const currentList = state.data?.[listKey];
  const updatedEntity = action.meta?.arg || {};

  if (!Array.isArray(currentList)) {
    state.data = action.payload;
    return;
  }

  if (updatedEntity.status === "Deactive") {
    state.data[listKey] = currentList.filter(
      (item) => item.id !== updatedEntity.id
    );

    if (state.data?.totalNoOfRecords?.totalNoOfRecords > 0) {
      state.data.totalNoOfRecords.totalNoOfRecords -= 1;
    }

    return;
  }

  if (updatedEntity.id) {
    state.data[listKey] = currentList.map((item) =>
      item.id === updatedEntity.id ? { ...item, ...updatedEntity } : item
    );
    return;
  }

  state.data = action.payload;
};

export const createEntityListStateUpdater = (listKey) => (state, action) =>
  updateEntityListState(state, action, listKey);

export const createEntityListSlice = ({
  name,
  extraState = {},
  reducers = {},
  asyncCases = [],
}) => {
  const createInitialState = () => createEntityListInitialState(extraState);

  return createSlice({
    name,
    initialState: createInitialState(),
    reducers: {
      ...paginationReducers,
      resetState: () => createInitialState(),
      ...reducers,
    },
    extraReducers: (builder) => {
      asyncCases.forEach(({ thunk, options }) => {
        addAsyncCases(builder, thunk, options);
      });
    },
  });
};
